// Maps the backend's `structured_data` payload (shipped on every tool_result
// WS frame) into one of our typed BlockOutput variants. Pure function — no
// React, no zustand — so it's easy to unit-test and reuse.
//
// The shape on the wire (from internal/skills/kula/search.go buildStructuredJSON):
//   { type: "table",
//     columns: ["Name", "Role", "Email"],
//     rows: [["Alice", "SWE", "a@b.com"], ...],
//     ids:  ["42", "17", ...],
//     total: 100,
//     entity: "contact" | "job" | "application" | "interview",
//     row_action: { action, label, params } | row_actions: [...]
//   }
//
// Entity drives the variant we emit. Unknown entities fall through to a
// generic actionResult block so the user at least sees something.

import type {
  BlockOutput,
  CandidateBlockData,
  CandidateSummary,
  ActionResultData,
  ClarifyBlockData,
  DataTableBlockData,
  EntityChipData,
  EntityKind,
  ResumeBlockData,
  ResumeFile,
} from '../types';

interface StructuredTable {
  type: string;
  columns?: string[];
  rows?: string[][];
  ids?: string[];
  /** Per-row deep link (e.g. "/contacts/24778"). Backend-supplied. */
  hrefs?: string[];
  /** Per-row structured fields (parallel to rows). Carries badge data the
   *  visible columns don't hold: years_experience, has_active_application,
   *  application_statuses, job_count. */
  row_meta?: Array<{
    years_experience?: number;
    has_active_application?: boolean;
    application_statuses?: string[];
    job_count?: number;
    /** Per-candidate "why this matched the query" line. Backend
     *  derives it from the nexus rerank_document (matched skills +
     *  highest-density resume snippet). Empty / undefined when the
     *  document had nothing to extract — the row just renders without
     *  it. */
    match_reason?: string;
    /** Coarse confidence label from the nexus llm_score — e.g.
     *  "Exact match" / "Strong match" / "Possible match". Renders as
     *  a small badge next to the candidate name. */
    match_badge?: string;
    /** Normalized 0..1 match score derived from llm_score / 10. */
    match_score?: number;
    /** Engagement pills nexus computed for this row. */
    badges?: { key?: string; label?: string; tone?: string }[];
    /** Pipeline milestones reached (e.g. ["lead","applied","interview"]). */
    milestones?: string[];
    /** Which aspects matched the query (e.g. ["note","profile"]). */
    matched_aspects?: string[];
    /** Matched-aspect evidence snippets (note/scorecard/transcript text). */
    evidence?: { aspect?: string; snippet?: string; rating?: number }[];
  }>;
  total?: number;
  /** Records nexus considers genuine matches for the filter universe. */
  matched_count?: number;
  /** Count of non-weak matches; rows beyond this fold behind a toggle. */
  strong_count?: number;
  /** "no_exact_matches" | "no_strong_matches" — present results cautiously. */
  match_warning?: string;
  /** Nexus's direct answer to a boolean/count/comparison question. */
  answer?: { type?: string; value?: unknown; explanation?: string };
  /** What the engine actually filtered on (transparency). Verbatim from nexus. */
  applied_filters?: {
    required_companies?: string[];
    required_skills?: string[];
    required_education?: string[];
    user_scope?: string;
  };
  /** Explicit format the user asked for ("table" | "cards"); else entity default. */
  format?: string;
  /** More rows exist in nexus's cached set (drives "Show more" pagination). */
  has_more?: boolean;
  cache_key?: string;
  next_offset?: number;
  entity?: string;
  // We only need the field, not the exact shape; kept loose intentionally.
  row_action?: unknown;
  row_actions?: unknown;
  /** Follow-up chips. Backend chooses both label and the user-message
   * the chip sends when clicked. */
  suggested_actions?: { label: string; prompt: string }[];
  /** Application_ids parallel to `ids` (same index). Only present for
   * application search results — ids[] holds contact_ids (for the inline
   * profile fetch), app_ids[] holds application_ids (for chips that act
   * on the application itself, like move-stage or open-application). */
  app_ids?: string[];
}

const isStructuredTable = (data: unknown): data is StructuredTable =>
  typeof data === 'object' && data !== null && 'type' in data;

const colIndex = (columns: string[] | undefined, name: string): number => {
  if (!columns) return -1;
  const lower = name.toLowerCase();
  return columns.findIndex((c) => c.toLowerCase() === lower);
};

const cell = (row: string[] | undefined, idx: number, fallback = ''): string =>
  row && idx >= 0 && idx < row.length ? (row[idx] ?? fallback) : fallback;

// Convert one row into a CandidateSummary using best-effort column lookup.
// The profileHref comes straight from the backend's `hrefs[i]` — the UI
// must never construct deep-link URLs itself.
const rowToCandidate = (
  row: string[],
  id: string,
  columns: string[] | undefined,
  profileHref: string | undefined,
  meta?: StructuredTable['row_meta'] extends Array<infer M> ? M : undefined
): CandidateSummary => ({
  id,
  // Backend now ships identical column shape for contact and
  // application searches (["Name", "Role", "Email"]), so a single
  // column lookup covers both.
  name: cell(row, colIndex(columns, 'Name')) || `#${id}`,
  headline:
    cell(row, colIndex(columns, 'Role')) ||
    cell(row, colIndex(columns, 'Title')) ||
    cell(row, colIndex(columns, 'Position')),
  tags: [],
  profileHref,
  email: cell(row, colIndex(columns, 'Email')) || undefined,
  matchReason: meta?.match_reason || undefined,
  matchBadge: meta?.match_badge || undefined,
  matchScore:
    typeof meta?.match_score === 'number' && meta.match_score > 0
      ? { value: meta.match_score, label: meta?.match_badge }
      : undefined,
  yearsExperience:
    typeof meta?.years_experience === 'number' && meta.years_experience > 0
      ? meta.years_experience
      : undefined,
  hasActiveApplication: meta?.has_active_application === true,
  applicationStatuses: meta?.application_statuses ?? [],
  jobCount: typeof meta?.job_count === 'number' ? meta.job_count : 0,
  badges: Array.isArray(meta?.badges)
    ? meta.badges
        .filter((b) => b && typeof b.label === 'string' && b.label !== '')
        .map((b) => ({
          key: typeof b.key === 'string' ? b.key : '',
          label: b.label as string,
          tone: typeof b.tone === 'string' ? b.tone : undefined,
        }))
    : undefined,
  milestones: Array.isArray(meta?.milestones) ? meta.milestones : undefined,
  matchedAspects: Array.isArray(meta?.matched_aspects)
    ? meta.matched_aspects
    : undefined,
  evidence: Array.isArray(meta?.evidence)
    ? meta.evidence
        .filter((e) => e && typeof e.snippet === 'string' && e.snippet !== '')
        .map((e) => ({
          aspect: typeof e.aspect === 'string' ? e.aspect : '',
          snippet: e.snippet as string,
          rating: typeof e.rating === 'number' ? e.rating : undefined,
        }))
    : undefined,
});

// Map backend `suggested_actions` into the BlockOutput shape. Backend now
// chooses both label and the prompt the chip sends; the frontend used to
// hardcode these per-entity but that meant a UI deploy for every wording
// tweak. Backend ownership = backend-only changes for new chips.
const mapSuggestedActions = (
  raw: StructuredTable['suggested_actions']
): CandidateBlockData['suggestedActions'] => {
  if (!raw || raw.length === 0) return undefined;
  return raw.map((a, i) => ({
    id: `sa-${i}`,
    label: a.label,
    kind: i === 0 ? 'primary' : 'secondary',
    promptTemplate: a.prompt,
  }));
};

const buildCandidateBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const hrefs = t.hrefs ?? [];
  const meta = t.row_meta ?? [];
  // Candidates render in payload order — agentforge sorts results by the
  // reranker's llm_score before building the payload (single source of truth),
  // so rows/ids/app_ids/row_meta already arrive highest-match-first.
  const candidates = rows.map((r, i) =>
    rowToCandidate(r, ids[i] ?? String(i), t.columns, hrefs[i], meta[i])
  );
  // Summary intentionally hides the underlying nexus total_count. Showing
  // "100 candidates — showing top 50" without a way to load the rest read
  // as a UI bug; users want a concrete count of what they're looking at,
  // not a teaser to invisible results.
  // Genuine-match count; clamp to [0, rows.length], ignore if it hides nothing.
  const rawStrong = t.strong_count;
  const strongCount =
    typeof rawStrong === 'number' && rawStrong >= 0 && rawStrong < rows.length
      ? rawStrong
      : undefined;
  const data: CandidateBlockData = {
    summary:
      rows.length === 1 ? `1 candidate.` : `Top ${rows.length} candidates.`,
    candidates,
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    // Pass through application_ids when present so chips can substitute
    // {first_appid} for kula_application_get calls.
    appIds: t.app_ids,
    strongCount,
    // Nexus's direct answer / match-warning / applied filters.
    answer:
      typeof t.answer?.explanation === 'string' && t.answer.explanation
        ? t.answer.explanation
        : undefined,
    matchWarning: t.match_warning,
    appliedFilterChips: appliedFilterChips(t.applied_filters),
    loadMore:
      t.has_more === true && typeof t.cache_key === 'string' && t.cache_key
        ? {
            cacheKey: t.cache_key,
            offset:
              typeof t.next_offset === 'number' ? t.next_offset : rows.length,
            remaining:
              typeof t.total === 'number' && t.total > rows.length
                ? t.total - rows.length
                : undefined,
          }
        : undefined,
    toolCallId,
  };
  return { type: 'candidate', data };
};

// nexus applied_filters → "Label: value" chips (e.g. "Company: JioStar").
const appliedFilterChips = (
  f: StructuredTable['applied_filters']
): string[] | undefined => {
  if (!f) return undefined;
  const chips: string[] = [];
  for (const c of f.required_companies ?? []) chips.push(`Company: ${c}`);
  for (const s of f.required_skills ?? []) chips.push(`Skill: ${s}`);
  for (const e of f.required_education ?? []) chips.push(`Education: ${e}`);
  return chips.length > 0 ? chips : undefined;
};

// Render candidate search rows as a data-table when the user asked for one.
const buildCandidateTableBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const meta = t.row_meta ?? [];
  const baseColumns = t.columns ?? ['Name', 'Role', 'Email'];
  // Append a Match column when rows carry relevance signal.
  const hasMatch = meta.some(
    (m) => m?.match_badge || typeof m?.match_score === 'number'
  );
  const columns = hasMatch ? [...baseColumns, 'Match'] : baseColumns;
  const tableRows = rows.map((r, i) => {
    if (!hasMatch) return r;
    const m = meta[i];
    const pct =
      typeof m?.match_score === 'number' && m.match_score > 0
        ? ` ${Math.round(m.match_score * 100)}%`
        : '';
    return [...r, `${m?.match_badge ?? ''}${pct}`.trim()];
  });
  const data: DataTableBlockData = {
    intro: '',
    variant: 'list',
    columns,
    rows: tableRows,
    ids: ids.length > 0 ? ids : undefined,
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'dataTable', data };
};

const ENTITY_KIND_MAP: Record<string, EntityKind> = {
  job: 'job',
  candidate: 'candidate',
  contact: 'candidate',
  application: 'candidate',
  user: 'user',
  stage: 'stage',
  department: 'department',
};

// Anything we don't have a bespoke block for yet (jobs returned in search
// results, application lists, interview lists, etc.) becomes an actionResult
// block with chips per row. The user still sees the data; the LLM still gets
// to summarize in prose.
const buildActionResultBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const total = t.total ?? rows.length;
  const kind: EntityKind = ENTITY_KIND_MAP[t.entity ?? ''] ?? 'job';
  const entities: EntityChipData[] = rows.map((r, i) => ({
    kind,
    id: ids[i] ?? String(i),
    label: cell(r, colIndex(t.columns, 'Name')) || `#${ids[i] ?? i}`,
  }));
  const data: ActionResultData = {
    summary:
      total > rows.length
        ? `${total} ${t.entity ?? 'result'}${total === 1 ? '' : 's'} — showing top ${rows.length}.`
        : `${total} ${t.entity ?? 'result'}${total === 1 ? '' : 's'}.`,
    entities,
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'actionResult', data };
};

// Shape emitted by the propose_clarify tool. Different envelope than the
// search table; we detect it before falling through to the table path.
interface StructuredClarify {
  type: 'clarify';
  intro?: unknown;
  pending?: unknown;
  options?: unknown;
  submit_label?: unknown;
}

const isStructuredClarify = (data: unknown): data is StructuredClarify =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type?: unknown }).type === 'clarify';

// Shape emitted when the agent needs structured form inputs (e.g. job title,
// department) rather than a multiple-choice pick.
interface StructuredClarifyForm {
  type: 'clarify_form';
  intro?: unknown;
  warning?: unknown;
  actions?: unknown;
  it_label?: unknown;
}

const isStructuredClarifyForm = (
  data: unknown
): data is StructuredClarifyForm =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type?: unknown }).type === 'clarify_form';

const buildClarifyFormBlock = (
  t: StructuredClarifyForm,
  toolCallId?: string
): BlockOutput | null => {
  const intro = typeof t.intro === 'string' ? t.intro : '';
  const warning = typeof t.warning === 'string' ? t.warning : '';
  if (!intro || !warning) return null;
  const rawActions = Array.isArray(t.actions) ? t.actions : [];
  const actions = rawActions
    .map((a) => {
      if (typeof a !== 'object' || a === null) return null;
      const obj = a as {
        key?: unknown;
        label?: unknown;
        prefill?: unknown;
        priority?: unknown;
        type?: unknown;
      };
      const key = typeof obj.key === 'string' ? obj.key : '';
      const label = typeof obj.label === 'string' ? obj.label : '';
      if (!key || !label) return null;
      return {
        key,
        label,
        prefill: typeof obj.prefill === 'string' ? obj.prefill : '',
        priority: typeof obj.priority === 'number' ? obj.priority : 0,
        type: typeof obj.type === 'string' ? obj.type : 'text',
      };
    })
    .filter((a): a is NonNullable<typeof a> => a !== null)
    .sort((a, b) => a.priority - b.priority);
  if (actions.length === 0) return null;
  return {
    type: 'clarifyForm',
    data: {
      intro,
      warning,
      actions,
      submitLabel: typeof t.it_label === 'string' ? t.it_label : 'Continue',
      toolCallId,
    },
  };
};

const buildClarifyBlock = (
  t: StructuredClarify,
  toolCallId?: string
): BlockOutput | null => {
  const intro = typeof t.intro === 'string' ? t.intro : '';
  const pending = typeof t.pending === 'string' ? t.pending : '';
  if (!intro || !pending) return null;
  const rawOptions = Array.isArray(t.options) ? t.options : [];
  const options = rawOptions
    .map((o) => {
      if (typeof o !== 'object' || o === null) return null;
      const obj = o as { value?: unknown; label?: unknown };
      const value = typeof obj.value === 'string' ? obj.value : '';
      const label = typeof obj.label === 'string' ? obj.label : '';
      if (!value || !label) return null;
      return { value, label };
    })
    .filter((o): o is { value: string; label: string } => o !== null);
  if (options.length === 0) return null;
  const data: ClarifyBlockData = {
    intro,
    pending,
    options,
    submitLabel:
      typeof t.submit_label === 'string' ? t.submit_label : undefined,
    toolCallId,
  };
  return { type: 'clarify', data };
};

// Resume envelope from kula_contact_resumes. Shape matches the tool's UI
// payload (see internal/skills/kula/contact.go).
interface StructuredResume {
  type: 'resume';
  contact_id?: unknown;
  resumes?: unknown;
}

const isStructuredResume = (data: unknown): data is StructuredResume =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type?: unknown }).type === 'resume';

const buildResumeBlock = (
  t: StructuredResume,
  toolCallId?: string
): BlockOutput | null => {
  const raw = Array.isArray(t.resumes) ? t.resumes : [];
  const resumes: ResumeFile[] = [];
  for (const r of raw) {
    if (typeof r !== 'object' || r === null) continue;
    const obj = r as Record<string, unknown>;
    const idRaw = obj.id;
    const id =
      typeof idRaw === 'number'
        ? idRaw
        : typeof idRaw === 'string'
          ? Number(idRaw)
          : NaN;
    const url = typeof obj.url === 'string' ? obj.url : '';
    const fileName =
      typeof obj.file_name === 'string' ? obj.file_name : `Resume ${id}`;
    if (!Number.isFinite(id) || !url) continue;
    const pc = (obj.parsed_content ?? {}) as Record<string, unknown>;
    const skills = Array.isArray(pc.SkillTags)
      ? (pc.SkillTags.filter((s) => typeof s === 'string') as string[])
      : [];
    const experience = Array.isArray(pc.Experience)
      ? (pc.Experience as Record<string, unknown>[]).map((e) => ({
          company: typeof e.Company === 'string' ? e.Company : undefined,
          jobTitle: typeof e.JobTitle === 'string' ? e.JobTitle : undefined,
          from: typeof e.From === 'string' ? e.From : undefined,
          to: typeof e.To === 'string' ? e.To : null,
          isCurrent: e.IsCurrentJob === 1 || e.IsCurrentJob === true,
          notes: typeof e.Notes === 'string' ? e.Notes : undefined,
        }))
      : [];
    const emails = Array.isArray(pc.Emails)
      ? (pc.Emails.filter((s) => typeof s === 'string') as string[])
      : [];
    const githubUrls = Array.isArray(pc.GithubURL)
      ? (pc.GithubURL.filter((s) => typeof s === 'string') as string[])
      : [];
    const otherUrls = Array.isArray(pc.OtherURLs)
      ? (pc.OtherURLs.filter((s) => typeof s === 'string') as string[])
      : [];
    const certifications = Array.isArray(pc.Certifications)
      ? (pc.Certifications.filter((s) => typeof s === 'string') as string[])
      : [];
    const education = Array.isArray(pc.Education)
      ? (pc.Education as Record<string, unknown>[]).map((ed) => ({
          school: typeof ed.School === 'string' ? ed.School : undefined,
          discipline:
            typeof ed.Discipline === 'string' ? ed.Discipline : undefined,
          qualification:
            typeof ed.Qualification === 'string' ? ed.Qualification : undefined,
          from: typeof ed.From === 'string' ? ed.From : undefined,
          to: typeof ed.To === 'string' ? ed.To : null,
        }))
      : [];
    const attached = Array.isArray(obj.ats_applications)
      ? (obj.ats_applications as Record<string, unknown>[])
          .map((a) => {
            const job = (a.job ?? {}) as Record<string, unknown>;
            const jId =
              typeof job.id === 'number'
                ? job.id
                : typeof job.id === 'string'
                  ? Number(job.id)
                  : NaN;
            const name = typeof job.name === 'string' ? job.name : '';
            // Carry the application id alongside the job — without it the
            // FE can only deep-link to the job page, not the specific
            // application's pipeline-stage view.
            const aId =
              typeof a.id === 'number'
                ? a.id
                : typeof a.id === 'string'
                  ? Number(a.id)
                  : NaN;
            return Number.isFinite(jId) && name
              ? {
                  id: jId as number,
                  name,
                  appId: Number.isFinite(aId) ? (aId as number) : undefined,
                }
              : null;
          })
          .filter(
            (x): x is { id: number; name: string; appId?: number } => x !== null
          )
      : [];
    resumes.push({
      id,
      url,
      fileName,
      contentType:
        typeof obj.content_type === 'string'
          ? obj.content_type
          : 'application/pdf',
      isPrimary: obj.is_primary === true,
      attachedJobs: attached,
      parsed: {
        name: typeof pc.Name === 'string' ? pc.Name : undefined,
        currentJobTitle:
          typeof pc.CurrentJobTitle === 'string'
            ? pc.CurrentJobTitle
            : undefined,
        totalExperienceYears:
          typeof pc.TotalExperienceInYears === 'number'
            ? pc.TotalExperienceInYears
            : undefined,
        skills,
        emails,
        mobileNumber:
          typeof pc.MobileNumber === 'string' ? pc.MobileNumber : undefined,
        location: typeof pc.Location === 'string' ? pc.Location : undefined,
        country: typeof pc.Country === 'string' ? pc.Country : undefined,
        linkedInUrl:
          typeof pc.LinkedInURL === 'string' ? pc.LinkedInURL : undefined,
        githubUrls,
        otherUrls,
        certifications,
        experience,
        education,
      },
    });
  }
  if (resumes.length === 0) return null;
  const contactIdRaw = t.contact_id;
  const contactId =
    typeof contactIdRaw === 'number'
      ? contactIdRaw
      : typeof contactIdRaw === 'string'
        ? Number(contactIdRaw)
        : 0;
  const data: ResumeBlockData = {
    contactId: Number.isFinite(contactId) ? contactId : 0,
    resumes,
    toolCallId,
  };
  return { type: 'resume', data };
};

// Generic-table envelope from the present_table tool. Distinct from the
// kula_search `type:"table"` payload (which assumes entity-shaped rows with
// name/role/email columns). data_table carries arbitrary columns/rows so
// the renderer is free to lay them out as a real table without trying to
// pattern-match candidate fields.
interface StructuredDataTable {
  type: 'data_table';
  intro?: unknown;
  variant?: unknown;
  columns?: unknown;
  rows?: unknown;
  ids?: unknown;
  footnote?: unknown;
  suggested_actions?: { label: string; prompt: string }[];
}

const isStructuredDataTable = (data: unknown): data is StructuredDataTable =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type?: unknown }).type === 'data_table';

const buildDataTableBlock = (
  t: StructuredDataTable,
  toolCallId?: string
): BlockOutput | null => {
  const intro = typeof t.intro === 'string' ? t.intro : '';
  const columns = Array.isArray(t.columns)
    ? t.columns.filter((c): c is string => typeof c === 'string')
    : [];
  const rows = Array.isArray(t.rows)
    ? t.rows
        .map((r) =>
          Array.isArray(r)
            ? r.map((cell) => (typeof cell === 'string' ? cell : String(cell)))
            : null
        )
        .filter((r): r is string[] => r !== null)
    : [];
  if (columns.length === 0 || rows.length === 0) return null;
  const ids = Array.isArray(t.ids)
    ? t.ids.filter((i): i is string => typeof i === 'string')
    : undefined;
  const rawVariant = typeof t.variant === 'string' ? t.variant : 'list';
  const variant: DataTableBlockData['variant'] =
    rawVariant === 'ranking' ||
    rawVariant === 'comparison' ||
    rawVariant === 'list' ||
    rawVariant === 'summary'
      ? rawVariant
      : 'list';
  const data: DataTableBlockData = {
    intro,
    variant,
    columns,
    rows,
    ids: ids && ids.length > 0 ? ids : undefined,
    footnote: typeof t.footnote === 'string' ? t.footnote : undefined,
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'dataTable', data };
};

// Inline email composer (kula_email_compose). Its own envelope, detected
// before the table path.
interface StructuredEmailCompose {
  type: 'email_compose';
  entity?: unknown;
  recipients?: unknown;
  subject?: unknown;
  body?: unknown;
  job_id?: unknown;
  // Stamped server-side once the email is sent (see MarkComposerSent) so a
  // reloaded composer shows its terminal "sent" state.
  sent?: unknown;
}

const isStructuredEmailCompose = (
  data: unknown
): data is StructuredEmailCompose =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type?: unknown }).type === 'email_compose';

const buildEmailComposeBlock = (
  t: StructuredEmailCompose,
  toolCallId?: string
): BlockOutput | null => {
  const entity = t.entity === 'application' ? 'application' : 'contact';
  const rawRecipients = Array.isArray(t.recipients) ? t.recipients : [];
  const recipients = rawRecipients
    .map((r) => {
      if (typeof r !== 'object' || r === null) return null;
      const obj = r as { id?: unknown; name?: unknown; email?: unknown };
      const id =
        typeof obj.id === 'number'
          ? String(obj.id)
          : typeof obj.id === 'string'
            ? obj.id
            : '';
      if (!id) return null;
      return {
        id,
        name: typeof obj.name === 'string' ? obj.name : undefined,
        email: typeof obj.email === 'string' ? obj.email : undefined,
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
  if (recipients.length === 0) return null;
  return {
    type: 'emailCompose',
    data: {
      entity,
      recipients,
      subject: typeof t.subject === 'string' ? t.subject : undefined,
      body: typeof t.body === 'string' ? t.body : undefined,
      jobId:
        typeof t.job_id === 'number'
          ? String(t.job_id)
          : typeof t.job_id === 'string'
            ? t.job_id
            : undefined,
      toolCallId,
      sent: t.sent === true,
    },
  };
};

/**
 * Convert backend `tool_result.structured_data` into a `BlockOutput`.
 * Returns `null` when the payload is missing, malformed, or empty so the
 * caller can skip pushing a useless block.
 */
export const structuredDataToBlock = (
  data: unknown,
  toolCallId?: string
): BlockOutput | null => {
  // Clarify pickers (propose_clarify tool) come in a different envelope —
  // detect first.
  if (isStructuredClarify(data)) {
    return buildClarifyBlock(data, toolCallId);
  }
  // Form-based clarification (propose_clarify with structured field inputs).
  if (isStructuredClarifyForm(data)) {
    return buildClarifyFormBlock(data, toolCallId);
  }
  // Inline email composer (kula_email_compose).
  if (isStructuredEmailCompose(data)) {
    return buildEmailComposeBlock(data, toolCallId);
  }
  // Resume payload from kula_contact_resumes.
  if (isStructuredResume(data)) {
    return buildResumeBlock(data, toolCallId);
  }
  // Generic table from present_table — must come before the kula_search
  // table branch since the discriminator is `type:"data_table"` and the
  // search-table path keys off the wider `type:"table"`.
  if (isStructuredDataTable(data)) {
    return buildDataTableBlock(data, toolCallId);
  }
  if (!isStructuredTable(data)) return null;
  const rows = data.rows ?? [];
  if (rows.length === 0) return null;
  // Applications are essentially candidates rendered in a job-context —
  // same name/position/status fields, same click-to-expand UX. The
  // dedicated `actionResult` block was deferred (see registry.ts) and
  // wouldn't render anyway; reusing the candidate card gives users the
  // rich row + Open-full-page link straight to the application's
  // pipeline-stage view (hrefs already include appId).
  if (
    data.entity === 'contact' ||
    data.entity === 'candidate' ||
    data.entity === 'application'
  ) {
    // Explicit table request; else rich candidate cards.
    if (data.format === 'table') {
      return buildCandidateTableBlock(data, toolCallId);
    }
    return buildCandidateBlock(data, toolCallId);
  }
  return buildActionResultBlock(data, toolCallId);
};
